import {
  Button,
  dropDetails,
  pack,
  StackIcon,
  Image,
} from '@dapperlabs/core-fe';
import {
  CardContent,
  CardMedia,
  Card,
  CardFooter,
  useHover,
  BoxGlow,
} from '@dapperlabs/skeleton-ui';
import { AnimatePresence, motion } from 'framer-motion';
import { useRouter } from 'next/router';
import React, { useCallback } from 'react';
import { useFeatureFlags } from 'src/lib/launchDarkly';
import { useTranslation } from 'src/lib/translations';
import {
  Flex,
  Text,
  useBreakpointValue,
  Box,
  NextLink,
  LinkBox,
} from 'src/theme';

interface MyPackListItemProps {
  distributionId: string;
  flowId: string;
  isOpen: boolean;
  isOwnProfile?: boolean;
  packImageUrl: string;
  packName: string;
  tier: string;
  totalMoments: number;
  username: string;
}

const slideUp = {
  closed: { transition: { type: 'tween' }, y: '0rem' },
  open: { transition: { type: 'tween' }, y: '-4rem', zIndex: 2 },
};

enum Orientation {
  LANDSCAPE = 'landscape',
  PORTRAIT = 'portrait',
}

const MyPackListItem = ({
  tier,
  totalMoments,
  flowId,
  packName,
  isOpen,
  packImageUrl,
  distributionId,
  username,
  isOwnProfile,
}: MyPackListItemProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const isMdUp = useBreakpointValue({ md: true });
  const layout = isMdUp ? Orientation.PORTRAIT : Orientation.LANDSCAPE;
  const [hoverRef, isHovered] = useHover();
  const { isFlowMaintenanceEnabled } = useFeatureFlags();
  const formatTier = (tierValue: string): string => {
    return tierValue.replaceAll('_', ' ').toLocaleLowerCase();
  };

  const redirectToPackOpening = useCallback(() => {
    router.push(pack(username, flowId));
  }, [username, flowId, router]);
  const isOpenedOrUnowned = isOpen || !isOwnProfile;
  const buttonLabel = isOpenedOrUnowned
    ? t('MyMoments.Packs.Opened.Button.Label')
    : t('MyMoments.Packs.UnopenedPack.Button.Label');

  return (
    <LinkBox key={distributionId} display='flex' data-testid={distributionId}>
      <BoxGlow
        layerStyle={!isOpenedOrUnowned ? 'packUnopened' : ''}
        borderRadius='xl'
      >
        <Card
          ref={hoverRef}
          borderRadius='xl'
          backgroundColor='black'
          layout={layout}
        >
          <NextLink href={dropDetails(distributionId)} legacyBehavior passHref>
            <CardMedia cursor='pointer'>
              <Flex justifyContent='center'>
                <Image
                  sx={{
                    aspectRatio: '1 / 1',
                  }}
                  w={300}
                  h='auto'
                  bg='black'
                  transform='scale(1.5)'
                  alt={t('PackDetail.PackImage')}
                  src={packImageUrl}
                />
              </Flex>
              {!isOpen && (
                <Box
                  position='absolute'
                  top={2}
                  left={2}
                  px={2}
                  py={1}
                  borderRadius='6px'
                  bgColor='gray.900'
                >
                  <Flex alignItems='center'>
                    <StackIcon width={3} height={3} mr={1} />
                    <Text fontFamily='mono' fontSize='xs'>
                      {t('MyMoments.Packs.UnopenedPack')}
                    </Text>
                  </Flex>
                </Box>
              )}
            </CardMedia>
          </NextLink>
          <CardContent
            as={motion.div}
            pt={3}
            animate={isHovered && isMdUp ? 'open' : 'closed'}
            variants={slideUp}
          >
            <NextLink
              href={dropDetails(distributionId)}
              legacyBehavior
              passHref
            >
              <Flex
                as='a'
                alignItems='start'
                justifyContent={isMdUp ? 'start' : 'center'}
                direction='column'
                width={!isMdUp ? '60%' : '100%'}
                mx={3}
                cursor='pointer'
              >
                <Box alignItems='center' justifyContent='center' mx={2}>
                  <Text
                    as='span'
                    color='#8A94A6'
                    fontFamily='main'
                    fontSize='sm'
                    fontWeight='bold'
                    textTransform='capitalize'
                  >
                    {formatTier(tier)}
                  </Text>
                  <Text
                    color='gray.50'
                    fontFamily='heading'
                    fontSize='4xl'
                    fontWeight='light'
                    letterSpacing='-0.02rem'
                  >
                    {packName}
                  </Text>
                  <Text
                    as='span'
                    fontFamily='main'
                    fontSize='sm'
                    fontWeight='light'
                  >
                    {totalMoments} {t('MyMoments.Packs.MomentsPerPack.Label')}
                  </Text>
                </Box>
              </Flex>
            </NextLink>
          </CardContent>
          <CardFooter
            as={motion.div}
            zIndex='1'
            border='none'
            animate={isHovered && isMdUp ? 'open' : 'closed'}
            variants={slideUp}
          >
            <AnimatePresence>
              {isHovered && isMdUp && (
                <motion.div
                  key={`view-cta-${distributionId}`}
                  transition={{ delay: 0.15 }}
                  animate={{
                    height: 'auto',
                    opacity: 1,
                    visibility: 'visible',
                    y: '0%',
                    zIndex: 2,
                  }}
                  exit={{
                    opacity: 0,
                  }}
                  initial={{
                    height: 0,
                    opacity: 0,
                    visibility: 'hidden',
                    y: '100%',
                  }}
                >
                  <Box pos='absolute' right={0} left={0}>
                    <Button
                      zIndex='docked'
                      w='full'
                      mt={4}
                      data-edition-id={distributionId}
                      disabled={isFlowMaintenanceEnabled && !isOpenedOrUnowned}
                      onClick={redirectToPackOpening}
                      variant={isOpen ? 'outline' : undefined}
                    >
                      {buttonLabel}
                    </Button>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
            {!isMdUp && (
              <Button
                zIndex='docked'
                w='full'
                mt={4}
                mb={2}
                data-edition-id={distributionId}
                disabled={isFlowMaintenanceEnabled && !isOpenedOrUnowned}
                onClick={redirectToPackOpening}
                variant={isOpen ? 'outline' : undefined}
              >
                {buttonLabel}
              </Button>
            )}
          </CardFooter>
        </Card>
      </BoxGlow>
    </LinkBox>
  );
};

export { MyPackListItem };
